import { useQuery } from '@tanstack/react-query';
import {
  fetchHasAnyMeeting,
  fetchMeetings,
  type CalendarMeetingItem,
} from '@/lib/meetings.ts';
import { addDays } from '@/lib/week.ts';
import type { AgendaWeek } from './useAgendaWeek.ts';

/** What the agenda shows under its toolbar. */
export type AgendaContent =
  | { kind: 'loading' }
  | { kind: 'failed' }
  /* Wundo was told nothing yet: no calendar and no meeting ever written down. */
  | { kind: 'nothingAtAll'; userId: string }
  | { kind: 'week'; meetings: CalendarMeetingItem[] };

export interface AgendaMeetings {
  content: AgendaContent;
  /** True while the empty state is up, or may still come up once the probe answers. */
  mayBeEmpty: boolean;
}

interface AgendaMeetingsOptions {
  userId: string | undefined;
  week: AgendaWeek;
  noCalendar: boolean;
}

/**
 * The week's meetings. Without a calendar an empty week also asks whether the
 * agenda ever held anything: the week only knows its own days. The answer is
 * cached under 'meetings', so it holds for every week until a meeting is added.
 */
export function useAgendaMeetings({
  userId,
  week,
  noCalendar,
}: AgendaMeetingsOptions): AgendaMeetings {
  const { weekStart, visibleDays } = week;
  const {
    data: meetings = [],
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetings', userId, weekStart.toISOString(), visibleDays],
    queryFn: () => fetchMeetings(userId!, weekStart, addDays(weekStart, visibleDays)),
    enabled: Boolean(userId),
  });

  /* A week that failed to load is not an empty one, and asks nothing further. */
  const emptyWeek = noCalendar && !isError && meetings.length === 0;
  const { data: hasAnyMeeting, isError: probeFailed } = useQuery({
    queryKey: ['meetings', userId, 'any'],
    queryFn: () => fetchHasAnyMeeting(userId!),
    enabled: Boolean(userId) && !isPending && emptyWeek,
  });
  const probing = emptyWeek && hasAnyMeeting === undefined && !probeFailed;
  const nothingAtAll = emptyWeek && hasAnyMeeting === false;

  return {
    content: agendaContent({
      loading: isPending || probing,
      failed: isError,
      nothingAtAll,
      userId,
      meetings,
    }),
    mayBeEmpty: probing || nothingAtAll,
  };
}

/* The probe waits behind the week's loading line, so neither answer flashes a page. */
function agendaContent(week: {
  loading: boolean;
  failed: boolean;
  nothingAtAll: boolean;
  userId: string | undefined;
  meetings: CalendarMeetingItem[];
}): AgendaContent {
  if (week.loading) return { kind: 'loading' };
  if (week.failed) return { kind: 'failed' };
  if (week.nothingAtAll && week.userId !== undefined) {
    return { kind: 'nothingAtAll', userId: week.userId };
  }
  return { kind: 'week', meetings: week.meetings };
}
